import { useEffect, useMemo, useState } from 'react';
import FormModal from '@/shared/components/form-modal';
import TrackingTimeline from '@/shared/components/TrackingTimeline';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useSettings } from '@/services/settings';
import { formatDate } from '@/utils/helpers';
import {
    darkPanelStyle,
    darkTextDim,
    errorBannerStyle,
    successBannerStyle,
    warningBannerStyle,
    darkSuccessText,
    darkWarningText,
    darkDangerText,
} from '@/shared/dark-inline-styles';
import {
    COURSE_CRM_SHIPPING_STATUSES,
    COURSE_CRM_STATUS_HINTS,
    COURSE_CRM_STATUS_LABELS,
    CourseCrmShippingStatus,
    CrmEnrollmentType,
} from '@/models/course-crm';
import { useCourseCrmClient, useUpdateCourseCrmClient, useUpdateCourseCrmNotifications, useUpdateCourseCrmShipment } from '@/services/course-crm';
import StatusChip, { TypeChip } from './status-chip';

interface Props {
    isOpen: boolean;
    userId?: string;
    toggle: () => void;
    /** Open with the tracking field focused — the table's "+ Add code" shortcut. */
    focusTracking?: boolean;
}

const labelStyle: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: darkTextDim, margin: '0 0 2px' };
const valueStyle: React.CSSProperties = { fontSize: 14, margin: '0 0 10px', whiteSpace: 'pre-line' };
const sectionTitle: React.CSSProperties = { margin: '16px 0 6px', fontWeight: 600, textAlign: 'left' };

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p style={labelStyle}>{label}</p>
        <p style={valueStyle}>{children}</p>
    </div>
);

const CHANNEL_LABEL: Record<string, string> = { email: 'Email', telegram: 'Telegram DM', inApp: 'In-app notification' };

/** The four happy-path steps the spec's stepper shows, in order. */
const STEPS: { key: CourseCrmShippingStatus; title: string; hint: string }[] = [
    { key: 'not_started', title: 'Signed up', hint: 'Platform sign-up recorded' },
    { key: 'awaiting_shipment', title: 'Sent to shipper', hint: 'Address handed to the shipping partner' },
    { key: 'shipped', title: 'Tracking code added', hint: 'In transit' },
    { key: 'delivered', title: 'Delivered', hint: 'Confirmed delivered' },
];

const ClientModal = ({ isOpen, userId, toggle, focusTracking }: Props) => {
    const { data: res, isLoading } = useCourseCrmClient(userId, isOpen);
    const detail = res?.data;
    const client = detail?.client;

    const { data: settingsRes } = useSettings();
    const carriers = settingsRes?.data?.shippingCarriers ?? [];

    const { mutateAsync: saveFields, isPending: savingFields } = useUpdateCourseCrmClient();
    const { mutateAsync: saveShipment, isPending: savingShipment } = useUpdateCourseCrmShipment();
    const { mutateAsync: saveChannels, isPending: savingChannels } = useUpdateCourseCrmNotifications();

    const [form, setForm] = useState({
        enrollmentType: '' as CrmEnrollmentType | '',
        cohort: '',
        referral: '',
        linkedinUrl: '',
        telegramHandle: '',
        telegramJoined: false,
        testimonialVideo: false,
        testimonialPost: false,
        notes: '',
        status: 'not_started' as CourseCrmShippingStatus,
        trackingCode: '',
        carrier: '',
        trackingUrl: '',
        shipmentNote: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [sendReport, setSendReport] = useState<{ channel: string; outcome: string; reason?: string }[] | null>(null);

    useEffect(() => {
        if (!isOpen || !client) return;
        setForm({
            enrollmentType: client.enrollmentType ?? '',
            cohort: client.cohortId ? '' : (client.cohortLabel ?? ''),
            referral: client.referral ?? '',
            linkedinUrl: client.linkedinUrl ?? '',
            telegramHandle: client.telegramHandle ?? '',
            telegramJoined: client.telegramJoined,
            testimonialVideo: client.testimonialVideo,
            testimonialPost: client.testimonialPost,
            notes: client.notes ?? '',
            status: client.shippingStatus,
            trackingCode: client.trackingCode ?? '',
            carrier: client.carrier ?? '',
            trackingUrl: client.trackingUrl ?? '',
            shipmentNote: client.shipmentNote ?? '',
        });
        setError(null);
        setSendReport(null);
    }, [isOpen, client, detail]);

    const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

    const currentStepIndex = useMemo(() => STEPS.findIndex((s) => s.key === client?.shippingStatus), [client?.shippingStatus]);

    const handleSave = async () => {
        if (!userId) return;
        setError(null);
        setSendReport(null);

        // Mirrors the API's rule at the field, so the admin is told before the
        // round trip rather than by a 400 afterwards.
        if (form.status === 'needs_attention' && !form.shipmentNote.trim()) {
            setError('Add a shipment note explaining why this needs attention.');
            return;
        }

        // callApi never throws on an API error — inspect result.status.
        const fieldsResult = await saveFields({
            userId,
            enrollmentType: form.enrollmentType,
            cohort: form.cohort.trim(),
            referral: form.referral.trim(),
            linkedinUrl: form.linkedinUrl.trim(),
            telegramHandle: form.telegramHandle.trim(),
            telegramJoined: form.telegramJoined,
            testimonialVideo: form.testimonialVideo,
            testimonialPost: form.testimonialPost,
            notes: form.notes,
        });
        if (fieldsResult.status === 'error') {
            setError(fieldsResult.error?.message ?? 'Failed to save the client details.');
            return;
        }

        const shipmentResult = await saveShipment({
            userId,
            status: form.status,
            trackingCode: form.trackingCode.trim(),
            carrier: form.carrier.trim(),
            trackingUrl: form.trackingUrl.trim(),
            note: form.shipmentNote,
        });
        if (shipmentResult.status === 'error') {
            setError(shipmentResult.error?.message ?? 'Failed to save the shipment.');
            return;
        }

        // Show what actually went out instead of closing on a silent send: a
        // channel that was switched on but unreachable reports `skipped`, and
        // that is the thing an admin needs to see.
        const sent = shipmentResult.data?.notifications ?? [];
        if (sent.length) {
            setSendReport(sent);
            return;
        }
        toggle();
    };

    const toggleChannel = async (channel: 'email' | 'telegram' | 'inApp', enabled: boolean) => {
        if (!userId) return;
        const result = await saveChannels({ userId, [channel]: enabled });
        if (result.status === 'error') setError(result.error?.message ?? 'Failed to save the notification switches.');
    };

    const addr = client?.address;
    const addressText = addr
        ? [addr.line1, addr.line2, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', '), addr.country].filter(Boolean).join('\n')
        : '—';

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={false}
            title={client ? client.name : 'Client'}
            loading={isLoading || savingFields || savingShipment || savingChannels}
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={() => undefined}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}
                    {sendReport && (
                        <div style={sendReport.every((r) => r.outcome === 'sent') ? successBannerStyle : warningBannerStyle}>
                            <strong>Saved.</strong> What went out:
                            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                                {sendReport.map((r) => (
                                    <li key={r.channel}>
                                        {CHANNEL_LABEL[r.channel] ?? r.channel} — <strong>{r.outcome}</strong>
                                        {r.reason ? ` (${r.reason})` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {client && (
                        <>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                <TypeChip type={client.enrollmentType} />
                                <StatusChip status={client.shippingStatus} />
                            </div>

                            {/* ── Contact (read-only; the profile page owns edits) ── */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left' }}>
                                <Field label="Email">{client.email || '— no email on file'}</Field>
                                <Field label="Phone">{client.phone ?? '—'}</Field>
                                <Field label="Country">{client.country ?? '—'}</Field>
                                <Field label="Signed up">{formatDate(client.platformSignupAt)}</Field>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Ship to">{addressText}</Field>
                                </div>
                                <Field label="Cohort">
                                    {client.cohortId ? (
                                        client.cohortTitle
                                    ) : client.cohortLabel ? (
                                        <>
                                            {client.cohortLabel} <span style={{ color: darkTextDim }}>(label only — not enrolled)</span>
                                        </>
                                    ) : (
                                        '— not recorded'
                                    )}
                                </Field>
                                <Field label="Order">{client.orderNumber ?? '— no board order yet'}</Field>
                            </div>
                            {!client.cohortId && (
                                <p style={{ margin: '-4px 0 8px', fontSize: 12, color: darkTextDim, textAlign: 'left' }}>
                                    Cohort membership is set on the Cohorts screen (Members). The label below is just for grouping in this table.
                                </p>
                            )}

                            {/* ── Shipping timeline ───────────────────────── */}
                            <p style={sectionTitle}>Shipping timeline</p>
                            <div style={{ ...darkPanelStyle, textAlign: 'left' }}>
                                {STEPS.map((step, i) => {
                                    const done = client.shippingStatus !== 'needs_attention' && i < currentStepIndex;
                                    const current = client.shippingStatus !== 'needs_attention' && i === currentStepIndex;
                                    const color = done ? darkSuccessText : current ? '#7da3ff' : darkTextDim;
                                    return (
                                        <div key={step.key} style={{ display: 'flex', gap: 10, padding: '4px 0' }}>
                                            <span style={{ color, fontWeight: 700, width: 14 }}>{done ? '✓' : i + 1}</span>
                                            <span>
                                                <span style={{ color, fontWeight: 600, fontSize: 13 }}>{step.title}</span>
                                                <span style={{ display: 'block', fontSize: 12, color: darkTextDim }}>
                                                    {step.key === 'shipped' && client.trackingCode ? client.trackingCode : step.hint}
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                                {client.shippingStatus === 'needs_attention' && (
                                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: darkDangerText }}>
                                        Flagged for manual review: {form.shipmentNote || 'see the shipment note below'}
                                    </p>
                                )}
                            </div>

                            {/* ── Shipment (editable) ─────────────────────── */}
                            <p style={sectionTitle}>Shipment</p>
                            <MaterialTextField
                                name="select"
                                select
                                label="Shipping status"
                                value={form.status}
                                onChange={(e) => set('status', e.target.value as CourseCrmShippingStatus)}
                            >
                                {COURSE_CRM_SHIPPING_STATUSES.map((s) => (
                                    <MaterialFormOption key={s} value={s}>
                                        {COURSE_CRM_STATUS_LABELS[s]}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <p style={{ margin: '-6px 0 10px', fontSize: 12, color: darkTextDim, textAlign: 'left' }}>
                                {COURSE_CRM_STATUS_HINTS[form.status]}
                            </p>
                            <div style={{ display: 'flex', gap: 16 }}>
                                {/* Carriers are runtime DATA (Settings), not a hardcoded
                                    enum — which carrier serves where is an operations fact. */}
                                {carriers.length > 0 ? (
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Carrier"
                                        value={form.carrier}
                                        onChange={(e) => set('carrier', e.target.value)}
                                    >
                                        <MaterialFormOption value="">No carrier</MaterialFormOption>
                                        {carriers.map((c) => (
                                            <MaterialFormOption key={c.name} value={c.name}>
                                                {c.name}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                ) : (
                                    <MaterialTextField
                                        label="Carrier (e.g. DHL)"
                                        value={form.carrier}
                                        onChange={(e) => set('carrier', e.target.value)}
                                    />
                                )}
                                <MaterialTextField
                                    autoFocus={focusTracking}
                                    label="Tracking code from the shipping partner"
                                    value={form.trackingCode}
                                    onChange={(e) => set('trackingCode', e.target.value)}
                                />
                            </div>
                            <MaterialTextField
                                label="Tracking URL (leave blank to derive it from the carrier)"
                                value={form.trackingUrl}
                                onChange={(e) => set('trackingUrl', e.target.value)}
                            />
                            <MaterialTextField
                                label="Shipment note (internal — required for Needs attention)"
                                multiline
                                value={form.shipmentNote}
                                onChange={(e) => set('shipmentNote', e.target.value)}
                            />
                            <p style={{ margin: '4px 0 0', fontSize: 12, color: darkTextDim, textAlign: 'left' }}>
                                Moving to <strong>Shipped</strong> or <strong>Delivered</strong> notifies the client on every channel below that is on
                                and reachable. Re-saving without a status change sends nothing.
                            </p>

                            {/* Carrier scan history. Empty until auto-tracking has
                                credentials, which is the current state everywhere —
                                the component says so rather than rendering nothing. */}
                            <div style={{ textAlign: 'left' }}>
                                <TrackingTimeline
                                    events={detail?.order?.trackingEvents}
                                    statusDetail={client?.trackingStatusDetail}
                                    lastCheckedAt={client?.trackingLastCheckedAt}
                                    lastError={client?.trackingLastError}
                                    hasTrackingCode={Boolean(client?.trackingCode)}
                                    orderId={detail?.order?.id}
                                />
                            </div>

                            {/* ── Notification channels ───────────────────── */}
                            <p style={sectionTitle}>Client notifications</p>
                            {detail?.channels ? (
                                <div style={{ ...darkPanelStyle, textAlign: 'left' }}>
                                    {(['email', 'telegram', 'inApp'] as const).map((ch) => {
                                        const info = detail.channels![ch];
                                        return (
                                            <div
                                                key={ch}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '7px 0',
                                                }}
                                            >
                                                <span>
                                                    <span style={{ fontSize: 13 }}>{CHANNEL_LABEL[ch]}</span>
                                                    {/* A reason only exists when there is something to
                                                        say, so it always reads as a caveat — including
                                                        the reachable-but-risky case, where we hold a
                                                        Telegram id from a channel join and Telegram may
                                                        still refuse the first message. */}
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            fontSize: 11.5,
                                                            color: info.reason ? darkWarningText : darkTextDim,
                                                        }}
                                                    >
                                                        {info.reason ?? info.target ?? 'Ready'}
                                                    </span>
                                                </span>
                                                {/* Disabled when the channel cannot deliver: a switch
                                                    that is on and does nothing is worse than a greyed
                                                    one, which is the whole point of showing the reason. */}
                                                <input
                                                    type="checkbox"
                                                    checked={info.enabled}
                                                    disabled={!info.available}
                                                    onChange={(e) => toggleChannel(ch, e.target.checked)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p style={{ fontSize: 12.5, color: darkTextDim, textAlign: 'left' }}>
                                    No board order yet — saving a shipment below creates one, and the channels appear then.
                                </p>
                            )}

                            {/* ── CRM labels ──────────────────────────────── */}
                            <p style={sectionTitle}>CRM details</p>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField
                                    name="select"
                                    select
                                    label="Type"
                                    value={form.enrollmentType}
                                    onChange={(e) => set('enrollmentType', e.target.value as CrmEnrollmentType | '')}
                                >
                                    <MaterialFormOption value="">Not set</MaterialFormOption>
                                    <MaterialFormOption value="paid">Paid</MaterialFormOption>
                                    <MaterialFormOption value="gift">Gift</MaterialFormOption>
                                </MaterialTextField>
                                <MaterialTextField
                                    label="Cohort label (grouping only)"
                                    value={form.cohort}
                                    onChange={(e) => set('cohort', e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField label="Referral" value={form.referral} onChange={(e) => set('referral', e.target.value)} />
                                <MaterialTextField
                                    label="Telegram handle"
                                    value={form.telegramHandle}
                                    onChange={(e) => set('telegramHandle', e.target.value)}
                                />
                            </div>
                            <MaterialTextField label="LinkedIn URL" value={form.linkedinUrl} onChange={(e) => set('linkedinUrl', e.target.value)} />
                            <div style={{ display: 'flex', gap: 18, margin: '8px 0', textAlign: 'left', fontSize: 13, flexWrap: 'wrap' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input type="checkbox" checked={form.telegramJoined} onChange={(e) => set('telegramJoined', e.target.checked)} />
                                    Joined the Telegram group
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={form.testimonialVideo}
                                        onChange={(e) => set('testimonialVideo', e.target.checked)}
                                    />
                                    Testimonial video
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={form.testimonialPost}
                                        onChange={(e) => set('testimonialPost', e.target.checked)}
                                    />
                                    Testimonial post
                                </label>
                            </div>
                            <MaterialTextField
                                label="Internal notes about the client"
                                multiline
                                value={form.notes}
                                onChange={(e) => set('notes', e.target.value)}
                            />

                            {/* ── Timeline ────────────────────────────────── */}
                            <p style={sectionTitle}>Activity &amp; notification log</p>
                            <div style={{ ...darkPanelStyle, textAlign: 'left', maxHeight: 220, overflowY: 'auto' }}>
                                {detail?.activity?.length ? (
                                    detail.activity.map((a) => (
                                        <div key={a.id} style={{ padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                            <span style={{ fontSize: 12.5 }}>{a.summary}</span>
                                            <span style={{ display: 'block', fontSize: 11, color: darkTextDim }}>
                                                {formatDate(a.occurredAt || a.created_at)}
                                                {a.actor?.adminEmail ? ` · ${a.actor.adminEmail}` : ''}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p style={{ margin: 0, fontSize: 12.5, color: darkTextDim }}>No events yet.</p>
                                )}
                            </div>
                        </>
                    )}
                </MaterialFormContainer>
            }
        />
    );
};

export default ClientModal;
